// AdminMerchantDetail.jsx
import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { adminAPI } from '../../services/api';
import { ArrowLeft, Ban, CheckCircle, Send, FileText, X, Download, Key, ArrowUpCircle, Image, Film, Mic, FileSpreadsheet, File, Mail, Briefcase, Cpu, DollarSign } from 'lucide-react';
import { format } from 'date-fns';
import toast from 'react-hot-toast';

export default function AdminMerchantDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showInvoice, setShowInvoice] = useState(false);
  const [invoiceForm, setInvoiceForm] = useState({ plan: 'professional', billingCycle: 'monthly', sendEmail: true });
  const [creating, setCreating] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [showEmail, setShowEmail] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [savingEmail, setSavingEmail] = useState(false);
  const [showBizType, setShowBizType] = useState(false);
  const [newBizType, setNewBizType] = useState('');
  const [savingBizType, setSavingBizType] = useState(false);
  const [showPlan, setShowPlan] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('');
  const [plans, setPlans] = useState([]);
  const [showMedia, setShowMedia] = useState(false);
  const [allowedMedia, setAllowedMedia] = useState([]);

  useEffect(() => {
    adminAPI.getMerchant(id).then(({ data }) => setData(data.data)).catch(() => {}).finally(() => setLoading(false));
    adminAPI.getPlans().then(({ data }) => setPlans(data.data || [])).catch(() => {});
  }, [id]);

  const toggleBlock = async () => {
    const newStatus = data.merchant.status === 'active' ? 'blocked' : 'active';
    try {
      await adminAPI.updateMerchantStatus(id, { status: newStatus, reason: 'Admin action' });
      setData(prev => ({ ...prev, merchant: { ...prev.merchant, status: newStatus } }));
      toast.success(`Merchant ${newStatus}`);
    } catch (_) {}
  };

  const createInvoice = async () => {
    setCreating(true);
    try {
      const { data: res } = await adminAPI.createInvoice(id, invoiceForm);
      setData(prev => ({ ...prev, invoices: [res.data, ...prev.invoices] }));
      setShowInvoice(false);
      toast.success(`Invoice ${res.data.invoiceNumber} created${invoiceForm.sendEmail ? ' and sent' : ''}`);
    } catch (_) {}
    finally { setCreating(false); }
  };

  const downloadPDF = async (inv) => {
    try {
      const { data } = await adminAPI.downloadInvoicePDF(inv._id);
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice-${inv.invoiceNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch (_) {}
  };

  const changePassword = async () => {
    if (newPassword.length < 8) return toast.error('Password must be at least 8 characters');
    try {
      await adminAPI.updateMerchantPassword(id, { password: newPassword });
      toast.success('Password updated successfully');
      setShowPassword(false);
      setNewPassword('');
    } catch (_) {}
  };

  const changeEmail = async () => {
    if (!/^\S+@\S+\.\S+$/.test(newEmail)) return toast.error('Enter a valid email');
    setSavingEmail(true);
    try {
      await adminAPI.updateMerchantEmail(id, { email: newEmail });
      setData(prev => ({ ...prev, merchant: { ...prev.merchant, email: newEmail } }));
      toast.success('Email updated successfully');
      setShowEmail(false);
      setNewEmail('');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to update email');
    } finally { setSavingEmail(false); }
  };

  const changeBusinessType = async () => {
    if (!newBizType) return;
    setSavingBizType(true);
    try {
      const { data: res } = await adminAPI.updateMerchantBusinessType(id, { businessType: newBizType });
      setData(prev => ({ ...prev, merchant: { ...prev.merchant, businessType: res.data.businessType } }));
      toast.success('Business type updated');
      setShowBizType(false);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to update business type');
    } finally { setSavingBizType(false); }
  };

  const saveMediaTypes = async () => {
    try {
      const { data: res } = await adminAPI.updateMerchantMediaTypes(id, { allowedMediaTypes: allowedMedia });
      setData(prev => ({ ...prev, merchant: { ...prev.merchant, allowedMediaTypes: res.data.allowedMediaTypes, enabledMediaTypes: res.data.enabledMediaTypes } }));
      setShowMedia(false);
      toast.success('Media types updated');
    } catch (_) { toast.error('Failed'); }
  };

  const toggleMediaType = (type) => {
    setAllowedMedia(prev => prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]);
  };

  const toggleExtractionFeature = async () => {
    const current = data?.merchant?.features?.extractionEnabled === true;
    try {
      const { data: res } = await adminAPI.updateMerchantFeatures(id, { extractionEnabled: !current });
      setData(prev => ({ ...prev, merchant: { ...prev.merchant, features: res.data.features } }));
      toast.success(!current ? 'Extraction enabled' : 'Extraction disabled');
    } catch (_) { toast.error('Failed'); }
  };

  const changePlan = async () => {
    if (!selectedPlan) return;
    try {
      const { data: res } = await adminAPI.updateMerchantPlan(id, { plan: selectedPlan });
      setData(prev => ({ ...prev, merchant: { ...prev.merchant, subscription: { ...prev.merchant.subscription, plan: selectedPlan }, limits: res.data.limits } }));
      setShowPlan(false);
      toast.success(`Plan changed to ${selectedPlan}`);
    } catch (_) {}
  };

  if (loading) return <div className="flex-center" style={{ height: '60vh' }}><div className="spinner" /></div>;
  if (!data) return <div className="page-body"><div className="alert alert-error">Merchant not found</div></div>;

  const { merchant, stats, invoices } = data;
  const tokenUsage = stats.tokenUsage || { inputTokens: 0, outputTokens: 0, totalTokens: 0, inputCost: 0, outputCost: 0, totalCost: 0, aiMessageCount: 0 };
  const fmtNum = (n) => Number(n || 0).toLocaleString();
  const fmtMoney = (n) => `$${Number(n || 0).toFixed(4)}`;

  return (
    <div>
      <div className="page-header">
        <div>
          <Link to="/admin/merchants" className="btn btn-ghost btn-sm" style={{ marginBottom: 8 }}><ArrowLeft size={14} /> Back</Link>
          <h1 className="page-title">{merchant.businessName}</h1>
          <p className="page-subtitle">{merchant.email}</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-secondary btn-sm" onClick={() => { setAllowedMedia(merchant.allowedMediaTypes || []); setShowMedia(true); }}><Image size={14} /> Media Types</button>
          <button
            className={`btn btn-sm ${merchant.features?.extractionEnabled ? 'btn-primary' : 'btn-secondary'}`}
            onClick={toggleExtractionFeature}
            title="Telegram image → Excel extraction">
            <FileSpreadsheet size={14} /> Extraction: {merchant.features?.extractionEnabled ? 'ON' : 'OFF'}
          </button>
          <button className="btn btn-secondary btn-sm" onClick={() => { setSelectedPlan(merchant.subscription?.plan || 'free'); setShowPlan(true); }}><ArrowUpCircle size={14} /> Change Plan</button>
          <button className="btn btn-secondary btn-sm" onClick={() => { setNewEmail(merchant.email || ''); setShowEmail(true); }}><Mail size={14} /> Change Email</button>
          <button className="btn btn-secondary btn-sm" onClick={() => { setNewBizType(merchant.businessType || 'catalog'); setShowBizType(true); }}><Briefcase size={14} /> Business Type</button>
          <button className="btn btn-secondary btn-sm" onClick={() => setShowPassword(true)}><Key size={14} /> Change Password</button>
          <button className="btn btn-primary btn-sm" onClick={() => setShowInvoice(true)}><FileText size={14} /> Create Invoice</button>
          <button className={`btn btn-sm ${merchant.status === 'active' ? 'btn-danger' : 'btn-secondary'}`} onClick={toggleBlock}>
            {merchant.status === 'active' ? <><Ban size={13} /> Block</> : <><CheckCircle size={13} /> Unblock</>}
          </button>
        </div>
      </div>
      <div className="page-body">
        <div className="card" style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
            <div className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Cpu size={16} /> AI Token Usage &amp; Cost
            </div>
            <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>
              GPT-4o mini · ${(0.00015 * 1000).toFixed(2)}/1M in · ${(0.00060 * 1000).toFixed(2)}/1M out
            </span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
            <div style={{ padding: 12, background: 'var(--bg)', borderRadius: 8, border: '1px solid var(--border)' }}>
              <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>Input Tokens</div>
              <div style={{ fontSize: '1.1rem', fontWeight: 700, marginTop: 4 }}>{fmtNum(tokenUsage.inputTokens)}</div>
              <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: 2 }}>{fmtMoney(tokenUsage.inputCost)}</div>
            </div>
            <div style={{ padding: 12, background: 'var(--bg)', borderRadius: 8, border: '1px solid var(--border)' }}>
              <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>Output Tokens</div>
              <div style={{ fontSize: '1.1rem', fontWeight: 700, marginTop: 4 }}>{fmtNum(tokenUsage.outputTokens)}</div>
              <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: 2 }}>{fmtMoney(tokenUsage.outputCost)}</div>
            </div>
            <div style={{ padding: 12, background: 'var(--bg)', borderRadius: 8, border: '1px solid var(--border)' }}>
              <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>Total Tokens</div>
              <div style={{ fontSize: '1.1rem', fontWeight: 700, marginTop: 4 }}>{fmtNum(tokenUsage.totalTokens)}</div>
              <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: 2 }}>{tokenUsage.aiMessageCount || 0} AI replies</div>
            </div>
            <div style={{ padding: 12, background: 'var(--primary-light)', borderRadius: 8, border: '1px solid var(--primary)' }}>
              <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 4 }}>
                <DollarSign size={12} /> Total Cost
              </div>
              <div style={{ fontSize: '1.1rem', fontWeight: 700, marginTop: 4, color: 'var(--primary)' }}>{fmtMoney(tokenUsage.totalCost)}</div>
              <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: 2 }}>across all replies</div>
            </div>
          </div>
        </div>
        <div className="grid-2" style={{ gap: 16, marginBottom: 20 }}>
          <div className="card">
            <div className="card-title" style={{ marginBottom: 14 }}>Merchant Info</div>
            {[
              ['Status', <span className={`badge ${merchant.status === 'active' ? 'badge-green' : 'badge-red'}`}>{merchant.status}</span>],
              ['Plan', <span className="badge badge-mauve" style={{ textTransform: 'capitalize' }}>{merchant.subscription?.plan}</span>],
              ['Sub Status', <span className="badge badge-blue">{merchant.subscription?.status}</span>],
              ['Phone', merchant.phone || '—'],
              ['Joined', format(new Date(merchant.createdAt), 'MMMM d, yyyy')],
              ['Messages', stats.messageCount.toLocaleString()],
              ['Customers', stats.customerCount.toLocaleString()],
            ].map(([label, val]) => (
              <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px solid var(--border)' }}>
                <span style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>{label}</span>
                <span style={{ fontSize: '0.82rem' }}>{val}</span>
              </div>
            ))}
          </div>
          <div className="card">
            <div className="card-title" style={{ marginBottom: 14 }}>Recent Invoices</div>
            {invoices.length === 0 ? <div className="text-muted">No invoices yet</div> : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {invoices.map(inv => (
                  <div key={inv._id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 10px', background: 'var(--bg)', borderRadius: 6 }}>
                    <div><div style={{ fontSize: '0.82rem', fontWeight: 600, fontFamily: 'var(--font-mono)' }}>{inv.invoiceNumber}</div>
                      <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{format(new Date(inv.createdAt), 'MMM d, yyyy')}</div></div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{ textAlign: 'right' }}><div style={{ fontWeight: 600 }}>${(inv.total / 100).toFixed(2)}</div>
                        <span className={`badge ${inv.status === 'paid' ? 'badge-green' : inv.status === 'overdue' ? 'badge-red' : 'badge-blue'}`} style={{ fontSize: '0.65rem' }}>{inv.status}</span></div>
                      <button className="btn-icon" title="Download PDF" onClick={() => downloadPDF(inv)} style={{ color: 'var(--primary)' }}><Download size={14} /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {showBizType && (
        <div className="modal-overlay" onClick={() => setShowBizType(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header"><span className="modal-title">Change Business Type</span><button className="modal-close" onClick={() => setShowBizType(false)}><X size={18} /></button></div>
            <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginBottom: 14 }}>Update business type for <strong>{merchant.businessName}</strong>. Current: <strong style={{ textTransform: 'capitalize' }}>{merchant.businessType || '—'}</strong></p>
            <div className="form-group">
              <label className="form-label">Business Type</label>
              <select className="form-input" value={newBizType} onChange={e => setNewBizType(e.target.value)}>
                <option value="order">Order / E-commerce</option>
                <option value="appointment">Appointment / Booking</option>
                <option value="lead">Lead Generation</option>
                <option value="support">Customer Support</option>
                <option value="catalog">Catalog / Info</option>
              </select>
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => setShowBizType(false)}>Cancel</button>
              <button className="btn btn-primary" disabled={savingBizType} onClick={changeBusinessType}><Briefcase size={13} /> {savingBizType ? 'Updating…' : 'Update'}</button>
            </div>
          </div>
        </div>
      )}

      {showEmail && (
        <div className="modal-overlay" onClick={() => setShowEmail(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header"><span className="modal-title">Change Email</span><button className="modal-close" onClick={() => setShowEmail(false)}><X size={18} /></button></div>
            <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginBottom: 14 }}>Update login email for <strong>{merchant.businessName}</strong>. Current: {merchant.email}</p>
            <div className="form-group">
              <label className="form-label">New Email</label>
              <input className="form-input" type="email" placeholder="merchant@example.com" value={newEmail} onChange={e => setNewEmail(e.target.value)} />
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => setShowEmail(false)}>Cancel</button>
              <button className="btn btn-primary" disabled={savingEmail} onClick={changeEmail}><Mail size={13} /> {savingEmail ? 'Updating…' : 'Update Email'}</button>
            </div>
          </div>
        </div>
      )}

      {showPassword && (
        <div className="modal-overlay" onClick={() => setShowPassword(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header"><span className="modal-title">Change Password</span><button className="modal-close" onClick={() => setShowPassword(false)}><X size={18} /></button></div>
            <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginBottom: 14 }}>Set a new password for <strong>{merchant.email}</strong></p>
            <div className="form-group">
              <label className="form-label">New Password</label>
              <input className="form-input" type="text" placeholder="Minimum 8 characters" value={newPassword} onChange={e => setNewPassword(e.target.value)} />
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => setShowPassword(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={changePassword}><Key size={13} /> Update Password</button>
            </div>
          </div>
        </div>
      )}

      {showInvoice && (
        <div className="modal-overlay" onClick={() => setShowInvoice(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header"><span className="modal-title">Create Invoice</span><button className="modal-close" onClick={() => setShowInvoice(false)}><X size={18} /></button></div>
            <div className="form-group"><label className="form-label">Plan</label>
              <select className="form-input" value={invoiceForm.plan} onChange={e => setInvoiceForm(p => ({ ...p, plan: e.target.value }))}>
                {['free', 'starter', 'professional', 'enterprise'].map(p => <option key={p} value={p} style={{ textTransform: 'capitalize' }}>{p}</option>)}
              </select></div>
            <div className="form-group"><label className="form-label">Billing Cycle</label>
              <select className="form-input" value={invoiceForm.billingCycle} onChange={e => setInvoiceForm(p => ({ ...p, billingCycle: e.target.value }))}>
                <option value="monthly">Monthly</option><option value="yearly">Yearly (save 20%)</option>
              </select></div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
              <button className={`toggle ${invoiceForm.sendEmail ? 'on' : ''}`} onClick={() => setInvoiceForm(p => ({ ...p, sendEmail: !p.sendEmail }))} />
              <span style={{ fontSize: '0.875rem' }}>Send invoice email to {merchant.email}</span>
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => setShowInvoice(false)}>Cancel</button>
              <button className="btn btn-primary" disabled={creating} onClick={createInvoice}><Send size={13} />{creating ? 'Creating…' : 'Create Invoice'}</button>
            </div>
          </div>
        </div>
      )}

      {showMedia && (
        <div className="modal-overlay" onClick={() => setShowMedia(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header"><span className="modal-title">Allowed Media Types</span><button className="modal-close" onClick={() => setShowMedia(false)}><X size={18} /></button></div>
            <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginBottom: 14 }}>
              Select which media types <strong>{merchant.businessName}</strong> can receive. Merchant can only enable types you allow here.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 }}>
              {[
                { key: 'image', label: 'Images', desc: 'JPG, PNG, WebP', icon: <Image size={18} style={{ color: '#22c55e' }} /> },
                { key: 'video', label: 'Videos', desc: 'MP4, MOV', icon: <Film size={18} style={{ color: '#6366f1' }} /> },
                { key: 'audio', label: 'Audio / Voice', desc: 'MP3, OGG, voice messages', icon: <Mic size={18} style={{ color: '#f59e0b' }} /> },
                { key: 'pdf', label: 'PDF', desc: 'PDF documents', icon: <FileText size={18} style={{ color: '#ef4444' }} /> },
                { key: 'document', label: 'Documents', desc: 'Word (DOCX, DOC)', icon: <File size={18} style={{ color: '#3b82f6' }} /> },
                { key: 'excel', label: 'Excel', desc: 'XLSX, XLS spreadsheets', icon: <FileSpreadsheet size={18} style={{ color: '#16a34a' }} /> },
                { key: 'csv', label: 'CSV', desc: 'CSV data files', icon: <FileText size={18} style={{ color: '#64748b' }} /> },
              ].map(type => {
                const isOn = allowedMedia.includes(type.key);
                return (
                  <div key={type.key} onClick={() => toggleMediaType(type.key)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', borderRadius: 10, cursor: 'pointer',
                      border: `2px solid ${isOn ? 'var(--primary)' : 'var(--border)'}`,
                      background: isOn ? 'var(--primary-light)' : 'transparent',
                      transition: 'all 0.15s',
                    }}>
                    <div style={{ width: 36, height: 36, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', background: isOn ? 'var(--primary-light)' : 'var(--bg)', border: '1px solid var(--border)' }}>
                      {type.icon}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: '0.88rem' }}>{type.label}</div>
                      <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{type.desc}</div>
                    </div>
                    <div style={{ width: 20, height: 20, borderRadius: 4, border: `2px solid ${isOn ? 'var(--primary)' : 'var(--border)'}`, background: isOn ? 'var(--primary)' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {isOn && <CheckCircle size={14} style={{ color: '#fff' }} />}
                    </div>
                  </div>
                );
              })}
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => setShowMedia(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveMediaTypes}><Image size={13} /> Save Media Types</button>
            </div>
          </div>
        </div>
      )}

      {showPlan && (
        <div className="modal-overlay" onClick={() => setShowPlan(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header"><span className="modal-title">Change Plan</span><button className="modal-close" onClick={() => setShowPlan(false)}><X size={18} /></button></div>
            <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginBottom: 14 }}>
              Change subscription plan for <strong>{merchant.businessName}</strong>
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 }}>
              {plans.length === 0 ? (
                <div style={{ padding: 16, textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                  No plans created yet. Go to Plans page to create plans first.
                </div>
              ) : plans.filter(p => p.isActive).map(plan => {
                const l = plan.limits || {};
                const fmtNum = (n) => n >= 999999 ? 'Unlimited' : n >= 1000 ? `${(n/1000).toFixed(0)}K` : n;
                const desc = plan.description || `${fmtNum(l.messagesPerMonth || 0)} msgs, ${fmtNum(l.aiRepliesPerMonth || 0)} AI, ${l.maxAgents || 0} agents, ${l.maxChannels || 0} channels`;
                return (
                  <div key={plan.slug} onClick={() => setSelectedPlan(plan.slug)}
                    style={{
                      padding: '12px 14px', borderRadius: 10, cursor: 'pointer',
                      border: `2px solid ${selectedPlan === plan.slug ? 'var(--primary)' : 'var(--border)'}`,
                      background: selectedPlan === plan.slug ? 'var(--primary-light)' : 'transparent',
                      transition: 'all 0.15s',
                    }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>{plan.name}</div>
                          {plan.price?.monthly > 0 && (
                            <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', fontWeight: 500 }}>
                              ${(plan.price.monthly / 100).toFixed(0)}/mo
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 2 }}>{desc}</div>
                        {plan.features?.length > 0 && (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
                            {plan.features.slice(0, 4).map((f, i) => (
                              <span key={i} style={{ fontSize: '0.65rem', padding: '1px 6px', borderRadius: 4, background: 'var(--bg)', color: 'var(--text-muted)', border: '1px solid var(--border)' }}>{f}</span>
                            ))}
                          </div>
                        )}
                      </div>
                      {merchant.subscription?.plan === plan.slug && (
                        <span className="badge badge-green" style={{ fontSize: '0.68rem', flexShrink: 0 }}>Current</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => setShowPlan(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={changePlan} disabled={!selectedPlan || selectedPlan === merchant.subscription?.plan}>
                <ArrowUpCircle size={13} /> {selectedPlan === merchant.subscription?.plan ? 'Current Plan' : 'Change Plan'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
